import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Mail,
  UserPlus,
  CheckCircle,
  Clock,
  XCircle,
  Copy,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import {
  createInvitations,
  getJobInvitations,
  deleteInvitation,
  parseEmailList,
  JobInvitation,
  createOrGetInvitationToken,
} from '@/lib/job-invitations';
import { supabase } from '@/lib/supabase-client';

interface InvitationManagerProps {
  jobId: string;
  jobTitle: string;
}

interface CandidateSession {
  id: string;
  candidate_name: string;
  candidate_email: string;
  final_score: number;
  communication_score: number;
  technical_score: number;
  created_at: string;
  user_id: string;
}

export default function InvitationManager({ jobId, jobTitle }: InvitationManagerProps) {
  const [invitations, setInvitations] = useState<JobInvitation[]>([]);
  const [topCandidates, setTopCandidates] = useState<CandidateSession[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const navigate = useNavigate();
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [loadingToken, setLoadingToken] = useState(false);

  useEffect(() => {
    fetchInvitations();
    getCurrentUser();
    fetchOrCreateInvitationToken();
    fetchTopCandidates();
  }, [jobId]);

  const getCurrentUser = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);

    if (user) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', user.id)
        .maybeSingle();

      setOrganizationId(profile?.organization_id || null);
    }
  };

  const fetchTopCandidates = async () => {
    setLoadingCandidates(true);
    try {
      const { data: recruiterProfiles } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('is_recruiter', true);

      const recruiterUserIds = recruiterProfiles?.map((profile) => profile.id) || [];

      const { data: sessions } = await supabase
        .from('interview_sessions')
        .select('id, candidate_name, candidate_email, ai_evaluation, created_at, user_id, status')
        .eq('job_posting_id', jobId)
        .eq('status', 'completed');

      const candidateSessions = (sessions || [])
        .filter((session: any) => {
          const isNotRecruiter = !session.user_id || !recruiterUserIds.includes(session.user_id);
          const hasScore = session.ai_evaluation?.finalScore || session.ai_evaluation?.overall_score;
          return isNotRecruiter && hasScore;
        })
        .map((session: any) => ({
          id: session.id,
          candidate_name: session.candidate_name,
          candidate_email: session.candidate_email,
          final_score: session.ai_evaluation?.finalScore || session.ai_evaluation?.overall_score || 0,
          communication_score: session.ai_evaluation?.communication || 0,
          technical_score: session.ai_evaluation?.technicalKnowledge || 0,
          created_at: session.created_at,
          user_id: session.user_id,
        }))
        .sort((a: any, b: any) => b.final_score - a.final_score)
        .slice(0, 10);

      setTopCandidates(candidateSessions as CandidateSession[]);
    } catch (error) {
      console.error('Error fetching top candidates:', error);
    } finally {
      setLoadingCandidates(false);
    }
  };

  const fetchOrCreateInvitationToken = async () => {
    setLoadingToken(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        console.error('User not authenticated');
        return;
      }

      const token = await createOrGetInvitationToken(jobId, user.id);
      setInvitationToken(token);
    } catch (error) {
      console.error('Error fetching invitation token:', error);
    } finally {
      setLoadingToken(false);
    }
  };

  const fetchInvitations = async () => {
    setLoading(true);
    try {
      const data = await getJobInvitations(jobId);
      setInvitations(data);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!emailInput.trim()) {
      setMessage({ type: 'error', text: 'Please enter at least one email address' });
      return;
    }

    if (!currentUserId) {
      setMessage({ type: 'error', text: 'You must be logged in to send invitations' });
      return;
    }

    const emails = parseEmailList(emailInput);

    if (emails.length === 0) {
      setMessage({ type: 'error', text: 'No valid email addresses found' });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const result = await createInvitations(jobId, emails, currentUserId);

      if (result.success > 0) {
        setMessage({
          type: 'success',
          text: `Successfully invited ${result.success} candidate${result.success > 1 ? 's' : ''}${
            result.failed > 0 ? `. ${result.failed} failed.` : ''
          }`,
        });
        setEmailInput('');
        fetchInvitations();
      } else {
        setMessage({
          type: 'error',
          text: `Failed to send invitations: ${result.errors.join(', ')}`,
        });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error sending invitations' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (invitationId: string) => {
    if (!confirm('Are you sure you want to delete this invitation?')) {
      return;
    }

    try {
      const success = await deleteInvitation(invitationId);
      if (success) {
        setMessage({ type: 'success', text: 'Invitation deleted successfully' });
        fetchInvitations();
      } else {
        setMessage({ type: 'error', text: 'Failed to delete invitation' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting invitation' });
    }
  };

  const handleCopyInviteLink = () => {
    if (!invitationToken) {
      setMessage({ type: 'error', text: 'Invitation link not ready yet' });
      return;
    }
    const link = `${window.location.origin}/jobs/${jobId}?invite=${invitationToken}`;
    navigator.clipboard.writeText(link);
    setMessage({ type: 'success', text: 'Invitation link copied to clipboard!' });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4 text-blue-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      default:
        return <XCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'accepted':
        return <Badge className="bg-blue-100 text-blue-800">Accepted</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const stats = {
    total: invitations.length,
    pending: invitations.filter((inv) => inv.status === 'pending').length,
    accepted: invitations.filter((inv) => inv.status === 'accepted').length,
    completed: invitations.filter((inv) => inv.status === 'completed').length,
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <UserPlus className="w-5 h-5 mr-2" />
            Invite Candidates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {message && (
            <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{message.text}</AlertDescription>
            </Alert>
          )}

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-blue-700" />
                  <p className="text-sm font-bold text-blue-900">Shareable Invitation Link</p>
                </div>
                <p className="text-xs text-blue-600 mb-1">
                  Share this link anywhere - website, email, LinkedIn, etc. Anyone with this link can view the job details and start an interview.
                </p>
                {loadingToken ? (
                  <div className="text-xs text-blue-700 italic">Generating link...</div>
                ) : invitationToken ? (
                  <p className="text-xs text-blue-800 break-all font-mono bg-white/50 p-2 rounded border border-blue-200">
                    {window.location.origin}/jobs/{jobId}?invite={invitationToken}
                  </p>
                ) : (
                  <p className="text-xs text-red-600">Unable to generate invitation link</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyInviteLink}
                disabled={!invitationToken || loadingToken}
                className="text-blue-700 hover:text-blue-900 hover:bg-blue-100"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or invite specific candidates by email</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter candidate email addresses (comma or line-separated)
            </label>
            <Textarea
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="candidate1@example.com, candidate2@example.com&#10;candidate3@example.com"
              className="min-h-[120px]"
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 mt-1">
              {emailInput ? `${parseEmailList(emailInput).length} valid email(s) found` : 'Enter emails separated by commas, semicolons, or new lines'}
            </p>
          </div>

          <Button
            onClick={handleInvite}
            disabled={submitting || !emailInput.trim()}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            <Mail className="w-4 h-4 mr-2" />
            {submitting ? 'Sending Invitations...' : 'Send Invitations'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Top Candidates</CardTitle>
            <Badge className="bg-blue-100 text-blue-800">
              {topCandidates.length} interviewed
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loadingCandidates ? (
            <div className="text-center py-8 text-gray-500">Loading candidates...</div>
          ) : topCandidates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No completed interviews yet. Candidates who complete interviews will appear here.
            </div>
          ) : (
            <div className="space-y-2">
              {topCandidates.map((candidate, index) => (
                <div
                  key={candidate.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/interviews/${candidate.id}`)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{candidate.candidate_name || 'Anonymous'}</p>
                      <p className="text-xs text-gray-500">{candidate.candidate_email}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(candidate.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">
                        {candidate.final_score?.toFixed(0) || 0}%
                      </div>
                      <div className="text-xs text-gray-500">Overall Score</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
